import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getOrg,
  getBillingStatus,
  createMembershipCheckout,
  openBillingPortal,
  verifyOrgCode,
} from '../auth/authApi';
import Layout from '../components/Layout';

const Portal = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [org, setOrg] = useState(null);
  const [billing, setBilling] = useState(null);
  // Membership / "Join an Organization" flow.
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [orgPreview, setOrgPreview] = useState(null); // { valid, organizationName }
  const [verifying, setVerifying] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [managing, setManaging] = useState(false);
  const navigate = useNavigate();
  const { user, deleteAccount } = useAuth();
  const userEmail = (user && (user.fullName || user.email)) || 'User';
  // Only org managers see the admin Console link; consumers ('member') don't.
  const isAdmin = !!(user && ['owner', 'admin', 'superadmin'].includes(user.role));
  // Show "Manage Membership" only for someone on an ACTIVE recurring plan. A
  // lapsed/canceled subscription (or a grandfathered exempt member with no
  // subscription at all) falls back to "Join an Organization" so they can
  // (re-)subscribe. `cancel_at_period_end` still counts as active until it ends.
  const sub = billing && billing.subscription;
  const hasSubscription = !!(sub && ['active', 'trialing', 'past_due'].includes(sub.status));
  // The paid "Join an Organization" upgrade is only for consumer users still in
  // the Range Drills platform org. Business-org members are provisioned by their
  // leadership's registration code and don't see it. Anyone with an active plan
  // still sees "Manage Membership" so they can manage/cancel.
  const inPlatformOrg = org?.type === 'platform';
  const showMembershipCard = hasSubscription || inPlatformOrg;

  useEffect(() => {
    window.scrollTo(0, 0);
    // Load org identity/branding (drives the Console link and, later, theming).
    getOrg().then(setOrg).catch(() => {});
    // Load billing status to decide between "Join an Organization" and "Manage".
    getBillingStatus().then(setBilling).catch(() => {});
  }, []);

  const openJoinModal = () => {
    setJoinCode('');
    setOrgPreview(null);
    setJoinError('');
    setShowJoinModal(true);
  };

  const closeJoinModal = () => {
    if (joining) return;
    setShowJoinModal(false);
  };

  // Preview which org a code resolves to before sending the user to payment.
  const handleVerifyCode = async () => {
    const code = joinCode.trim();
    if (!code) return;
    setVerifying(true);
    setJoinError('');
    setOrgPreview(null);
    try {
      const result = await verifyOrgCode(code);
      if (!result || !result.valid) {
        setJoinError('That code isn’t valid. Double-check it and try again.');
      } else {
        setOrgPreview(result);
      }
    } catch (e) {
      setJoinError('Could not verify the code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleStartCheckout = async () => {
    const code = joinCode.trim();
    if (!code) return;
    setJoining(true);
    setJoinError('');
    try {
      const { url } = await createMembershipCheckout(code);
      // Redirect straight to Stripe's hosted Checkout page. Same experience as
      // redirectToCheckout, but needs no publishable key / Stripe.js.
      if (!url) throw new Error('no_checkout_url');
      window.location.href = url;
    } catch (e) {
      const map = {
        invalid_organization_code: 'That code isn’t valid. Double-check it and try again.',
        not_a_joinable_org: 'That code can’t be used to join here.',
        not_eligible: 'This upgrade is only available to Range Drills members.',
        billing_not_configured: 'Payments aren’t set up yet. Please contact support.',
      };
      setJoinError(map[e.message] || 'Could not start checkout. Please try again.');
      setJoining(false);
    }
  };

  const handleManageMembership = async () => {
    setManaging(true);
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch (e) {
      setManaging(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setDeleteError('');

    try {
      // Server-side delete cascades routines + tokens and revokes the session.
      await deleteAccount();
      closeDeleteModal();

      const successMessage = document.createElement('div');
      successMessage.innerHTML = '✓ Account deleted successfully';
      successMessage.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background-color: #4CAF50;
        color: white;
        padding: 15px 30px;
        border-radius: 8px;
        font-size: 1.1em;
        font-weight: 600;
        z-index: 10000;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      `;
      document.body.appendChild(successMessage);

      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Delete account error:', error);
      setIsDeleting(false);
      setDeleteError('Error deleting account. Please try again or contact support at info@rangedrills.io');
    }
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmText('');
    setDeleteError('');
    setIsDeleting(false);
  };

  const confirmDelete = () => {
    if (deleteConfirmText.toLowerCase() === 'delete') {
      handleDeleteAccount();
      closeDeleteModal();
    }
  };

  return (
    <Layout>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <section id="wrapper">
        <div className="inner" style={{ paddingTop: "40px" }}>
          <div style={{ marginBottom: "20px" }}>
            <h2 className="major" style={{ textAlign: "center" }}>
              Welcome to Range Drills Portal
            </h2>
          </div>
          
          <div className="content" style={{ 
            maxWidth: "800px", 
            margin: "0 auto", 
            padding: "30px", 
            backgroundColor: "rgba(27, 31, 34, 0.85)",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            marginTop: "20px",
            marginBottom: "40px"
          }}>
            <p style={{ 
                fontSize: "1.1em", 
                marginBottom: "30px",
                color: "#fff",
                opacity: "0.9"
              }}>
                Hello, <strong>{userEmail}</strong>! Welcome to your user portal.
              </p>

              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "20px",
                marginTop: "30px"
              }}>
                <div style={{
                  padding: "25px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.2)"
                }}>
                  <h3 style={{ 
                    marginTop: "0", 
                    marginBottom: "15px",
                    color: "#FF5722"
                  }}>
                    Training Videos
                  </h3>
                  <p style={{ 
                    margin: "0", 
                    opacity: "0.9",
                    lineHeight: "1.6"
                  }}>
                    Check out our training videos to help you improve your shooting skills.
                  </p>
                  <Link
                    to="/videos"
                    style={{
                      display: "inline-block",
                      marginTop: "15px",
                      padding: "10px 20px",
                      borderRadius: "6px",
                      backgroundColor: "#FF5722",
                      color: "#fff",
                      textDecoration: "none",
                      fontWeight: "600",
                      transition: "background-color 0.3s ease"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#e64a19";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#FF5722";
                    }}
                  >
                    Go to Videos
                  </Link>
                </div>

                {isAdmin && (
                  <div style={{
                    padding: "25px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    border: "1px solid rgba(255, 255, 255, 0.2)"
                  }}>
                    <h3 style={{
                      marginTop: "0",
                      marginBottom: "15px",
                      color: "#FF5722"
                    }}>
                      Admin Console
                    </h3>
                    <p style={{
                      margin: "0",
                      opacity: "0.9",
                      lineHeight: "1.6"
                    }}>
                      Manage {org ? org.name : 'your organization'} — members, join code, and branding.
                    </p>
                    <Link
                      to="/console"
                      style={{
                        display: "inline-block",
                        marginTop: "15px",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        backgroundColor: "#FF5722",
                        color: "#fff",
                        textDecoration: "none",
                        fontWeight: "600",
                        transition: "background-color 0.3s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#e64a19";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#FF5722";
                      }}
                    >
                      Go to Console
                    </Link>
                  </div>
                )}

                {/* Membership / Join an Organization — only for platform-org
                    consumers, or anyone with an active plan to manage. */}
                {showMembershipCard && (
                <div style={{
                  padding: "25px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.2)"
                }}>
                  <h3 style={{
                    marginTop: "0",
                    marginBottom: "15px",
                    color: "#FF5722"
                  }}>
                    {hasSubscription ? 'Your Organization Access' : 'Join an Organization'}
                  </h3>
                  <p style={{
                    margin: "0",
                    opacity: "0.9",
                    lineHeight: "1.6"
                  }}>
                    {hasSubscription
                      ? 'Manage or cancel your organization access and update your payment details.'
                      : 'Have a join code from your organization? Become a member to unlock premium training.'}
                  </p>
                  {hasSubscription ? (
                    <button
                      onClick={handleManageMembership}
                      disabled={managing}
                      style={{
                        display: "inline-block",
                        marginTop: "15px",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        border: "none",
                        backgroundColor: "#FF5722",
                        color: "#fff",
                        fontWeight: "600",
                        fontSize: "inherit",
                        fontFamily: "inherit",
                        cursor: managing ? "not-allowed" : "pointer",
                        opacity: managing ? 0.6 : 1,
                        transition: "background-color 0.3s ease"
                      }}
                    >
                      {managing ? 'Opening…' : 'Manage Access'}
                    </button>
                  ) : (
                    <button
                      onClick={openJoinModal}
                      style={{
                        display: "inline-block",
                        marginTop: "15px",
                        padding: "10px 20px",
                        borderRadius: "6px",
                        border: "none",
                        backgroundColor: "#FF5722",
                        color: "#fff",
                        fontWeight: "600",
                        fontSize: "inherit",
                        fontFamily: "inherit",
                        cursor: "pointer",
                        transition: "background-color 0.3s ease"
                      }}
                      onMouseEnter={(e) => { e.target.style.backgroundColor = "#e64a19"; }}
                      onMouseLeave={(e) => { e.target.style.backgroundColor = "#FF5722"; }}
                    >
                      Join an Organization
                    </button>
                  )}
                </div>
                )}
              </div>

              {/* <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "20px",
                marginTop: "30px"
              }}>
                <div style={{
                  padding: "25px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.2)"
                }}>
                  <h3 style={{ 
                    marginTop: "0", 
                    marginBottom: "15px",
                    color: "#FF5722"
                  }}>
                    Training Sessions
                  </h3>
                  <p style={{ 
                    margin: "0", 
                    opacity: "0.9",
                    lineHeight: "1.6"
                  }}>
                    View and manage your training sessions, track your progress, and review your shooting history.
                  </p>
                </div>

                <div style={{
                  padding: "25px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.2)"
                }}>
                  <h3 style={{ 
                    marginTop: "0", 
                    marginBottom: "15px",
                    color: "#FF5722"
                  }}>
                    Statistics
                  </h3>
                  <p style={{ 
                    margin: "0", 
                    opacity: "0.9",
                    lineHeight: "1.6"
                  }}>
                    Access detailed analytics about your shooting performance and improvement over time.
                  </p>
                </div>

                <div style={{
                  padding: "25px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.2)"
                }}>
                  <h3 style={{ 
                    marginTop: "0", 
                    marginBottom: "15px",
                    color: "#FF5722"
                  }}>
                    Settings
                  </h3>
                  <p style={{ 
                    margin: "0", 
                    opacity: "0.9",
                    lineHeight: "1.6"
                  }}>
                    Manage your account settings, preferences, and notification preferences.
                  </p>
                </div>
              </div> */}

              {/* <div style={{
                marginTop: "40px",
                padding: "20px",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                borderRadius: "8px"
              }}>
                <h3 style={{ 
                  marginTop: "0", 
                  marginBottom: "15px",
                  color: "#fff"
                }}>
                  Quick Actions
                </h3>
                <div style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "15px"
                }}>
                  <button style={{
                    padding: "12px 24px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: "#FF5722",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "1em",
                    fontWeight: "500",
                    transition: "background-color 0.3s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#e64a19";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#FF5722";
                  }}
                  >
                    Start New Session
                  </button>
                  <button style={{
                    padding: "12px 24px",
                    borderRadius: "6px",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "1em",
                    fontWeight: "500",
                    transition: "all 0.3s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                  }}
                  >
                    View History
                  </button>
                  <button style={{
                    padding: "12px 24px",
                    borderRadius: "6px",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "1em",
                    fontWeight: "500",
                    transition: "all 0.3s ease"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                  }}
                  >
                    Download App
                  </button>
                </div>
              </div> */}
          </div>

          {/* Account Management Section */}
          <div style={{
            maxWidth: "800px",
            margin: "0 auto",
            padding: "30px",
            backgroundColor: "rgba(27, 31, 34, 0.85)",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
            marginTop: "20px",
            marginBottom: "40px"
          }}>
            <h3 style={{ 
              marginTop: "0", 
              marginBottom: "20px",
              color: "#fff"
            }}>
              Account Management
            </h3>
            
            <div style={{
              padding: "20px",
              backgroundColor: "rgba(255, 0, 0, 0.1)",
              borderRadius: "8px",
              border: "1px solid rgba(255, 0, 0, 0.3)"
            }}>
              <h4 style={{ 
                marginTop: "0", 
                marginBottom: "10px",
                color: "#ff5555"
              }}>
                Delete Account
              </h4>
              <p style={{ 
                margin: "0 0 15px 0", 
                opacity: "0.9",
                lineHeight: "1.6",
                fontSize: "0.95em"
              }}>
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <button
                onClick={openDeleteModal}
                style={{
                  padding: "10px 20px",
                  borderRadius: "6px",
                  border: "1px solid #ff5555",
                  backgroundColor: "transparent",
                  color: "#ff5555",
                  cursor: "pointer",
                  fontSize: "0.9em",
                  fontWeight: "600",
                  transition: "all 0.3s ease"
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "#ff5555";
                  e.target.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#ff5555";
                }}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div 
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
              padding: "20px"
            }}
            onClick={closeDeleteModal}
          >
            <div 
              style={{
                backgroundColor: "rgba(27, 31, 34, 0.98)",
                borderRadius: "12px",
                padding: "40px",
                maxWidth: "500px",
                width: "100%",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
                border: "1px solid rgba(255, 85, 85, 0.3)"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ 
                marginTop: "0", 
                marginBottom: "20px",
                color: "#ff5555",
                textAlign: "center"
              }}>
                ⚠️ Delete Account
              </h2>
              
              <p style={{ 
                marginBottom: "20px",
                lineHeight: "1.8",
                textAlign: "center",
                fontSize: "1.05em"
              }}>
                <strong>This action cannot be undone.</strong>
              </p>
              
              <p style={{ 
                marginBottom: "25px",
                lineHeight: "1.6",
                opacity: "0.9"
              }}>
                Deleting your account will permanently remove:
              </p>
              
              <ul style={{
                marginBottom: "25px",
                paddingLeft: "20px",
                lineHeight: "1.8",
                opacity: "0.9"
              }}>
                <li>All your training sessions and data</li>
                <li>Your account information and settings</li>
                <li>Access to all premium features</li>
                <li>Your training history and statistics</li>
              </ul>

              <p style={{ 
                marginBottom: "15px",
                lineHeight: "1.6",
                fontWeight: "600"
              }}>
                To confirm, please type <span style={{ color: "#ff5555" }}>delete</span> below:
              </p>

              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'delete' to confirm"
                disabled={isDeleting}
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  borderRadius: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  color: "#fff",
                  fontSize: "1em",
                  marginBottom: "15px",
                  boxSizing: "border-box",
                  opacity: isDeleting ? 0.5 : 1
                }}
              />

              {/* Error Message */}
              {deleteError && (
                <div style={{
                  padding: "12px 15px",
                  borderRadius: "6px",
                  backgroundColor: "rgba(255, 85, 85, 0.2)",
                  border: "1px solid rgba(255, 85, 85, 0.4)",
                  color: "#ff8888",
                  marginBottom: "15px",
                  fontSize: "0.95em",
                  lineHeight: "1.5"
                }}>
                  {deleteError}
                </div>
              )}

              <div style={{
                display: "flex",
                gap: "15px",
                justifyContent: "center"
              }}>
                <button
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                  style={{
                    padding: "12px 30px",
                    borderRadius: "6px",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#fff",
                    cursor: isDeleting ? "not-allowed" : "pointer",
                    fontSize: "1em",
                    fontWeight: "500",
                    transition: "all 0.3s ease",
                    opacity: isDeleting ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isDeleting) {
                      e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDeleting) {
                      e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                    }
                  }}
                >
                  Cancel
                </button>
                
                <button
                  onClick={confirmDelete}
                  disabled={deleteConfirmText.toLowerCase() !== 'delete' || isDeleting}
                  style={{
                    padding: "12px 30px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: (deleteConfirmText.toLowerCase() === 'delete' && !isDeleting) ? "#ff5555" : "rgba(255, 85, 85, 0.3)",
                    color: (deleteConfirmText.toLowerCase() === 'delete' && !isDeleting) ? "#fff" : "rgba(255, 255, 255, 0.5)",
                    cursor: (deleteConfirmText.toLowerCase() === 'delete' && !isDeleting) ? "pointer" : "not-allowed",
                    fontSize: "1em",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                  }}
                  onMouseEnter={(e) => {
                    if (deleteConfirmText.toLowerCase() === 'delete' && !isDeleting) {
                      e.target.style.backgroundColor = "#cc0000";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (deleteConfirmText.toLowerCase() === 'delete' && !isDeleting) {
                      e.target.style.backgroundColor = "#ff5555";
                    }
                  }}
                >
                  {isDeleting ? (
                    <>
                      <span style={{
                        display: "inline-block",
                        width: "16px",
                        height: "16px",
                        border: "2px solid rgba(255, 255, 255, 0.3)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite"
                      }}></span>
                      Deleting...
                    </>
                  ) : (
                    'Delete Account'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Join an Organization Modal */}
        {showJoinModal && (
          <div
            style={{
              position: "fixed",
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.85)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
              padding: "20px"
            }}
            onClick={closeJoinModal}
          >
            <div
              style={{
                backgroundColor: "rgba(27, 31, 34, 0.98)",
                borderRadius: "12px",
                padding: "40px",
                maxWidth: "500px",
                width: "100%",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
                border: "1px solid rgba(255, 87, 34, 0.3)"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginTop: 0, marginBottom: "15px", color: "#FF5722", textAlign: "center" }}>
                Join an Organization
              </h2>
              <p style={{ marginBottom: "20px", lineHeight: "1.6", opacity: "0.9", textAlign: "center" }}>
                Enter the join code you were given. You’ll be taken to secure checkout to unlock your
                organization access, and added to the organization once payment succeeds.
              </p>

              <input
                type="text"
                value={joinCode}
                onChange={(e) => { setJoinCode(e.target.value); setOrgPreview(null); setJoinError(''); }}
                placeholder="Enter join code"
                disabled={joining}
                style={{
                  width: "100%",
                  padding: "12px 15px",
                  borderRadius: "6px",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  backgroundColor: "rgba(0, 0, 0, 0.3)",
                  color: "#fff",
                  fontSize: "1em",
                  marginBottom: "12px",
                  boxSizing: "border-box"
                }}
              />

              {orgPreview && orgPreview.valid && (
                <div style={{
                  padding: "12px 15px",
                  borderRadius: "6px",
                  backgroundColor: "rgba(76, 175, 80, 0.15)",
                  border: "1px solid rgba(76, 175, 80, 0.4)",
                  color: "#a5d6a7",
                  marginBottom: "15px",
                  fontSize: "0.95em"
                }}>
                  You’ll be joining <strong>{orgPreview.organizationName}</strong>.
                </div>
              )}

              {joinError && (
                <div style={{
                  padding: "12px 15px",
                  borderRadius: "6px",
                  backgroundColor: "rgba(255, 85, 85, 0.2)",
                  border: "1px solid rgba(255, 85, 85, 0.4)",
                  color: "#ff8888",
                  marginBottom: "15px",
                  fontSize: "0.95em",
                  lineHeight: "1.5"
                }}>
                  {joinError}
                </div>
              )}

              <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  onClick={closeJoinModal}
                  disabled={joining}
                  style={{
                    padding: "12px 24px",
                    borderRadius: "6px",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#fff",
                    cursor: joining ? "not-allowed" : "pointer",
                    fontSize: "1em",
                    fontWeight: "500"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifyCode}
                  disabled={verifying || joining || !joinCode.trim()}
                  style={{
                    padding: "12px 24px",
                    borderRadius: "6px",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: "#fff",
                    cursor: (verifying || joining || !joinCode.trim()) ? "not-allowed" : "pointer",
                    fontSize: "1em",
                    fontWeight: "500",
                    opacity: (verifying || joining || !joinCode.trim()) ? 0.5 : 1
                  }}
                >
                  {verifying ? 'Checking…' : 'Verify Code'}
                </button>
                <button
                  onClick={handleStartCheckout}
                  disabled={joining || !joinCode.trim()}
                  style={{
                    padding: "12px 24px",
                    borderRadius: "6px",
                    border: "none",
                    backgroundColor: (joining || !joinCode.trim()) ? "rgba(255, 87, 34, 0.4)" : "#FF5722",
                    color: "#fff",
                    cursor: (joining || !joinCode.trim()) ? "not-allowed" : "pointer",
                    fontSize: "1em",
                    fontWeight: "600"
                  }}
                >
                  {joining ? 'Redirecting…' : 'Continue to Payment'}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default Portal;

